import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { authConfig } from './config';
import { useNavigate } from 'react-router-dom';

// Configure Amplify
const configureAmplify = () => {
  // In browser environments, use the current origin for redirects
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : null;
  
  // Determine redirect URLs based on environment
  const redirectUrls = import.meta.env.MODE !== 'development' 
    ? ['https://dashboard.scorecheck.au/']
    : currentOrigin ? [`${currentOrigin}/`] : ['http://localhost:3000/'];
  
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: authConfig.userPoolId,
        userPoolClientId: authConfig.userPoolWebClientId,
        identityPoolId: authConfig.identityPoolId,
        allowGuestAccess: false,
        signUpVerificationMethod: 'link',
        loginWith: {
          oauth: {
            domain: authConfig.oauth.domain,
            scopes: authConfig.oauth.scope,
            // Use dynamic redirect URLs
            redirectSignIn: redirectUrls,
            redirectSignOut: redirectUrls,
            responseType: 'code' // Must be 'code' or 'token' in v6
          }
        }
      }
    },
    API: {
      REST: {
        dashboard: {
          endpoint: authConfig.apiEndpoint,
        },
      }
    },
  });
  
  if (currentOrigin) {
    console.log('Configured auth redirects to:', redirectUrls);
  }
};

interface AuthUser {
  username: string;
  userId: string;
  email?: string;
}

interface AuthSession {
  tokens: {
    accessToken?: string;
    idToken?: string;
    refreshToken?: string;
  };
}

interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: () => void;
  logout: () => Promise<void>;
  getToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    configureAmplify();
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Auth check timeout')), 5000)
      );
      
      const currentUser = await Promise.race([
        getCurrentUser(),
        timeoutPromise
      ]);
      
      if (currentUser) {
        const currentSession = await Promise.race([
          fetchAuthSession(),
          timeoutPromise
        ]);
        
        if (currentSession) {
          // Extract user details from the current user object
          setUser({
            username: (currentUser as any).username,
            userId: (currentUser as any).userId,
            email: (currentUser as any).email || (currentUser as any).attributes?.email
          });
          
          setSession(currentSession as AuthSession);
          setIsAuthenticated(true);
          
          // Check if we need to redirect after successful login
          const redirectPath = sessionStorage.getItem('redirectAfterLogin');
          if (redirectPath && redirectPath !== '/') {
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(redirectPath);
          }
        }
      }
    } catch (error) {
      console.log('Auth check result: User not authenticated or auth check failed');
      setUser(null);
      setSession(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const signIn = () => {
    signInWithRedirect();
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setSession(null);
      setIsAuthenticated(false);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getToken = (): string | null => {
    if (session?.tokens?.idToken) {
      return session.tokens.idToken;
    }
    return null;
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        session, 
        isAuthenticated, 
        isLoading,
        signIn, 
        logout,
        getToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}